import { CLOUDINARY_CLOUD_NAME, CLOUDINARY_VIDEO_FOLDER } from '../config/cloudinary';

const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || '';

/**
 * Direct-to-Cloudinary unsigned upload (browser → Cloudinary, bypassing the
 * Flask backend entirely) — used for admin-uploaded video clips, since the
 * backend runs on Vercel where a locally-saved file wouldn't survive the next
 * deploy. Requires REACT_APP_CLOUDINARY_UPLOAD_PRESET to be set to an
 * "unsigned" preset configured in the Cloudinary dashboard.
 */
export async function uploadVideoToCloudinary(file, { onProgress } = {}) {
  if (!UPLOAD_PRESET) {
    throw new Error(
      'Video upload isn’t configured yet — set REACT_APP_CLOUDINARY_UPLOAD_PRESET (see .env.example) to an unsigned Cloudinary upload preset.'
    );
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);
  if (CLOUDINARY_VIDEO_FOLDER) formData.append('folder', CLOUDINARY_VIDEO_FOLDER);

  const url = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/video/upload`;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      let body;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        body = null;
      }
      if (xhr.status >= 200 && xhr.status < 300 && body?.secure_url) {
        resolve(body.secure_url);
      } else {
        reject(new Error(body?.error?.message || `Upload failed (${xhr.status}).`));
      }
    };

    xhr.onerror = () => reject(new Error('Upload failed — network error.'));
    xhr.send(formData);
  });
}

/**
 * Direct-to-Cloudinary unsigned upload for admin-uploaded images. The backend's
 * own /uploads endpoint saves to local disk, which doesn't survive Vercel's
 * ephemeral filesystem — this bypasses it the same way uploadVideoToCloudinary
 * does for clips. Requires the same REACT_APP_CLOUDINARY_UPLOAD_PRESET.
 */
export async function uploadImageToCloudinary(file, { onProgress } = {}) {
  if (!UPLOAD_PRESET) {
    throw new Error(
      'Image upload isn’t configured yet — set REACT_APP_CLOUDINARY_UPLOAD_PRESET (see .env.example) to an unsigned Cloudinary upload preset.'
    );
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);
  if (CLOUDINARY_VIDEO_FOLDER) formData.append('folder', CLOUDINARY_VIDEO_FOLDER);

  const url = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      let body;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        body = null;
      }
      if (xhr.status >= 200 && xhr.status < 300 && body?.secure_url) {
        resolve(body.secure_url);
      } else {
        reject(new Error(body?.error?.message || `Upload failed (${xhr.status}).`));
      }
    };

    xhr.onerror = () => reject(new Error('Upload failed — network error.'));
    xhr.send(formData);
  });
}
